import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CareersHero from "./BlogComponents/CareersHero";


const Careers = () => {
  const [jobPostings, setJobPostings] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    cover_letter: '',
    cv: null,
    linkedin_profile: '',
    portfolio_url: '',
    job: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isFormVisible, setIsFormVisible] = useState(false);
  
  const formRef = useRef(null);
  const applicationSectionRef = useRef(null);

  // Your color theme
  const PRIMARY_BLUE = '#1E3A8A';
  const ACCENT_BLUE = '#3B82F6';
  const GOLD = '#FFD700';
  const LIGHT_BLUE = '#E6F7FF';
  const WHITE = '#FFFFFF';

  const API_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8000/api/job-postings/'
    : 'https://www.dlamsoft.co.za/api/job-postings/';

  const API_APPLY = process.env.NODE_ENV === 'development'
    ? 'http://127.0.0.1:8000/api/job-applications/'
    : 'https://www.dlamsoft.co.za/api/job-applications/';

  useEffect(() => {
    fetchJobPostings();
    function checkMobile() {
      if (typeof window === "undefined") return;
      setIsMobile(window.innerWidth < 768);
    }
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchJobPostings = async () => {
    try {
      const response = await fetch(API_URL);
      const data = await response.json();
      setJobPostings(data);
    } catch (error) {
      console.error('Error fetching job postings:', error);
    }
  };

  const handleJobSelect = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, job: job.id }));
    setIsFormVisible(true);
    
    // Scroll to application form
    setTimeout(() => {
      if (applicationSectionRef.current) {
        applicationSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleCloseForm = () => {
    setIsFormVisible(false);
    setSelectedJob(null);
    setSubmitStatus(null);
    
    // Optional: Reset form data when closing
    // setFormData({
    //   full_name: '',
    //   email: '',
    //   phone: '',
    //   cover_letter: '',
    //   cv: null,
    //   linkedin_profile: '',
    //   portfolio_url: '',
    //   job: null
    // });
    // document.getElementById('cv-upload').value = '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitStatus) setSubmitStatus(null);
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, cv: e.target.files[0] }));
    if (submitStatus) setSubmitStatus(null);
  };

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Validation - ALL fields now required
    if (!formData.full_name.trim() || !formData.email.trim() || !formData.phone.trim() || 
        !formData.cover_letter.trim() || !formData.cv || 
        !formData.linkedin_profile.trim() || !formData.portfolio_url.trim()) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      setIsSubmitting(false);
      return;
    }

    if (!formData.job) {
      setSubmitStatus({ type: 'error', message: 'Please select a job position.' });
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid email address.' });
      setIsSubmitting(false);
      return;
    }

    // URL validation for LinkedIn and Portfolio
    if (!isValidUrl(formData.linkedin_profile)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid LinkedIn URL.' });
      setIsSubmitting(false);
      return;
    }

    if (!isValidUrl(formData.portfolio_url)) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid Portfolio URL.' });
      setIsSubmitting(false);
      return;
    }

    // Ensure LinkedIn URL is from LinkedIn domain
    if (!formData.linkedin_profile.includes('linkedin.com/in/') && 
        !formData.linkedin_profile.includes('linkedin.com/company/')) {
      setSubmitStatus({ type: 'error', message: 'Please enter a valid LinkedIn profile URL (should contain linkedin.com/in/ or linkedin.com/company/).' });
      setIsSubmitting(false);
      return;
    }

    // Check file size (max 10MB)
    if (formData.cv && formData.cv.size > 10 * 1024 * 1024) {
      setSubmitStatus({ type: 'error', message: 'CV file size must be less than 10MB.' });
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const csrfToken = getCookie('csrftoken');
      const requestOptions = {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken || '',
        },
        body: formDataToSend,
        credentials: 'include',
      };

      const response = await fetch(API_APPLY, requestOptions);

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Thank you for your application! We will review it and get back to you soon.' 
        });
        // Reset form
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          cover_letter: '',
          cv: null,
          linkedin_profile: '',
          portfolio_url: '',
          job: null
        });
        setSelectedJob(null);
        // Clear file input
        if (document.getElementById('cv-upload')) {
          document.getElementById('cv-upload').value = '';
        }
        // Auto-close form after successful submission (optional)
        setTimeout(() => {
          setIsFormVisible(false);
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'There was an error submitting your application. Please try again.';
        
        if (response.status === 403) errorMessage = 'CSRF verification failed. Please refresh the page and try again.';
        else if (response.status === 400) {
          if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            errorMessage = errorData.non_field_errors[0];
          } else {
            errorMessage = 'Please check your input and try again.';
          }
        }
        else if (response.status === 500) errorMessage = 'Server error. Please try again later.';
        
        setSubmitStatus({ type: 'error', message: errorMessage });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'There was a network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter job postings by category
  const categories = ['all', 'full_time', 'part_time', 'contract', 'remote'];
  const filteredJobs = activeCategory === 'all' 
    ? jobPostings 
    : jobPostings.filter(job => job.job_type === activeCategory);

  // Dynamic grid layout based on number of jobs
  const getGridTemplateColumns = () => {
    if (isMobile) return '1fr';
    
    const jobCount = filteredJobs.length;
    if (jobCount === 0) return '1fr';
    if (jobCount === 1) return '1fr'; // Single job takes full width
    if (jobCount === 2) return 'repeat(2, 1fr)'; // Two jobs side by side
    if (jobCount === 3) return 'repeat(3, 1fr)'; // Three jobs side by side
    return 'repeat(auto-fill, minmax(350px, 1fr))'; // Default for 4+ jobs
  };

  // Dynamic max width for single job
  const getJobCardMaxWidth = () => {
    if (isMobile) return '100%';
    if (filteredJobs.length === 1) return '800px'; // Wider for single job
    return 'none';
  };

  // Styles object - properly defined with all functions
  const styles = {
    main: {
      minHeight: '100vh',
      backgroundColor: LIGHT_BLUE,
    },
    jobsSection: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '40px 20px' : '60px 40px',
    },
    sectionTitle: {
      textAlign: 'center',
      fontSize: isMobile ? '2.2rem' : '3rem',
      fontWeight: '800',
      color: PRIMARY_BLUE,
      marginBottom: '20px',
      fontFamily: "'Poppins', sans-serif",
    },
    sectionSubtitle: {
      textAlign: 'center',
      fontSize: isMobile ? '1.1rem' : '1.3rem',
      color: ACCENT_BLUE,
      marginBottom: '40px',
      maxWidth: '700px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: '1.6',
    },
    categoryFilter: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '15px',
      marginBottom: '50px',
    },
    categoryButton: (isActive) => ({
      padding: '12px 25px',
      borderRadius: '30px',
      border: `2px solid ${isActive ? GOLD : PRIMARY_BLUE}`,
      background: isActive ? GOLD : 'transparent',
      color: isActive ? PRIMARY_BLUE : PRIMARY_BLUE,
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      outline: 'none',
      textTransform: 'capitalize',
      fontFamily: "'Poppins', sans-serif",
      letterSpacing: '0.5px',
    }),
    jobsGrid: {
      display: 'grid',
      gridTemplateColumns: getGridTemplateColumns(),
      gap: '30px',
      marginBottom: '80px',
      justifyContent: filteredJobs.length === 1 && !isMobile ? 'center' : 'stretch',
      alignItems: 'stretch',
    },
    jobCard: (isSelected) => ({
      background: WHITE,
      borderRadius: '15px',
      padding: '30px',
      boxShadow: isSelected ? '0 10px 30px rgba(30, 58, 138, 0.2)' : '0 5px 20px rgba(0, 0, 0, 0.08)',
      border: `2px solid ${isSelected ? GOLD : 'transparent'}`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      maxWidth: getJobCardMaxWidth(),
      margin: filteredJobs.length === 1 && !isMobile ? '0 auto' : '0',
      display: 'flex',
      flexDirection: 'column',
      height: filteredJobs.length === 1 && !isMobile ? 'auto' : '100%',
      minHeight: filteredJobs.length === 1 && !isMobile ? '400px' : '350px',
    }),
    jobTypeBadge: (type) => {
      const colors = {
        full_time: '#10B981',
        part_time: '#F59E0B',
        contract: '#8B5CF6',
        remote: '#3B82F6'
      };
      return {
        display: 'inline-block',
        background: colors[type] || PRIMARY_BLUE,
        color: WHITE,
        padding: '6px 15px',
        borderRadius: '20px',
        fontSize: '0.8rem',
        fontWeight: '600',
        marginBottom: '20px',
        alignSelf: 'flex-start',
      };
    },
    jobTitle: {
      fontSize: isMobile || filteredJobs.length > 1 ? '1.5rem' : '2rem',
      fontWeight: '700',
      color: PRIMARY_BLUE,
      marginBottom: '15px',
      fontFamily: "'Poppins', sans-serif",
      lineHeight: '1.3',
    },
    jobLocation: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: ACCENT_BLUE,
      fontSize: isMobile || filteredJobs.length > 1 ? '1rem' : '1.1rem',
      marginBottom: '20px',
      fontWeight: '500',
    },
    jobDescription: {
      color: '#4B5563',
      fontSize: isMobile || filteredJobs.length > 1 ? '1rem' : '1.1rem',
      lineHeight: '1.7',
      marginBottom: '25px',
      flex: 1,
    },
    jobMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '15px',
      marginBottom: '25px',
      paddingTop: '15px',
      borderTop: '1px solid #E5E7EB',
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      color: '#6B7280',
      fontSize: '0.85rem',
    },
    applyButton: {
      width: '100%',
      padding: filteredJobs.length === 1 && !isMobile ? '18px' : '15px',
      background: PRIMARY_BLUE,
      color: WHITE,
      border: 'none',
      borderRadius: '10px',
      fontSize: filteredJobs.length === 1 && !isMobile ? '1.1rem' : '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      letterSpacing: '0.5px',
      marginTop: 'auto',
    },
    noJobsMessage: {
      textAlign: 'center',
      fontSize: '1.2rem',
      color: ACCENT_BLUE,
      padding: '60px 20px',
      background: WHITE,
      borderRadius: '15px',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
    },
    applicationSection: {
      marginTop: '50px',
    },
    applicationFormSection: {
      background: WHITE,
      borderRadius: '15px',
      padding: isMobile ? '30px 20px' : '40px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      maxWidth: '800px',
      margin: '0 auto',
    },
    formHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '15px',
    },
    formTitle: {
      fontSize: isMobile ? '1.8rem' : '2rem',
      fontWeight: '700',
      color: PRIMARY_BLUE,
      fontFamily: "'Poppins', sans-serif",
      margin: 0,
    },
    closeButton: {
      padding: '10px 20px',
      background: 'transparent',
      color: ACCENT_BLUE,
      border: `2px solid ${ACCENT_BLUE}`,
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    selectedJobTitle: {
      textAlign: 'center',
      fontSize: isMobile ? '1.1rem' : '1.2rem',
      color: ACCENT_BLUE,
      marginBottom: '30px',
      fontWeight: '500',
    },
    form: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    formRow: {
      display: isMobile ? 'block' : 'flex',
      gap: '30px',
      marginBottom: '25px',
    },
    formGroup: {
      flex: 1,
      marginBottom: isMobile ? '25px' : '0',
    },
    label: {
      display: 'block',
      marginBottom: '10px',
      fontWeight: '600',
      color: PRIMARY_BLUE,
      fontSize: '1rem',
    },
    required: {
      color: '#EF4444',
    },
    input: {
      width: '100%',
      padding: '15px',
      border: `2px solid #E5E7EB`,
      borderRadius: '10px',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      outline: 'none',
    },
    fileInput: {
      width: '100%',
      padding: '15px',
      border: `2px dashed ${ACCENT_BLUE}`,
      borderRadius: '10px',
      fontSize: '1rem',
      background: LIGHT_BLUE,
      cursor: 'pointer',
    },
    fileInfo: {
      fontSize: '0.85rem',
      color: '#6B7280',
      marginTop: '5px',
      marginLeft: '5px',
    },
    textarea: {
      width: '100%',
      padding: '15px',
      border: `2px solid #E5E7EB`,
      borderRadius: '10px',
      fontSize: '1rem',
      minHeight: '150px',
      resize: 'vertical',
      fontFamily: "'Poppins', sans-serif",
      outline: 'none',
      transition: 'all 0.3s ease',
    },
    submitButton: {
      width: '100%',
      padding: '18px',
      background: PRIMARY_BLUE,
      color: WHITE,
      border: 'none',
      borderRadius: '10px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '20px',
      fontFamily: "'Poppins', sans-serif",
      letterSpacing: '1px',
    },
    statusMessage: {
      textAlign: 'center',
      marginTop: '20px',
      padding: '15px',
      borderRadius: '10px',
      fontWeight: '500',
      fontSize: '1rem',
    },
    showFormButton: {
      display: 'block',
      margin: '40px auto',
      padding: '15px 40px',
      background: GOLD,
      color: PRIMARY_BLUE,
      border: 'none',
      borderRadius: '10px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Poppins', sans-serif",
      boxShadow: '0 5px 20px rgba(255, 215, 0, 0.3)',
    },
  };

  return (
    <main style={styles.main}>
      
      <section style={styles.jobsSection}>
        <motion.h2 
          style={styles.sectionTitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Open Positions
        </motion.h2>
        
        <motion.p 
          style={styles.sectionSubtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          Join our innovative team and build the future with us
        </motion.p>

        <motion.div 
          style={styles.categoryFilter}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map(category => (
            <button
              key={category}
              style={styles.categoryButton(activeCategory === category)}
              onClick={() => setActiveCategory(category)}
              onMouseEnter={(e) => {
                if (activeCategory !== category) {
                  e.currentTarget.style.background = GOLD;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeCategory !== category) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {category.replace('_', ' ').toUpperCase()}
            </button>
          ))}
        </motion.div>

        {filteredJobs.length > 0 ? (
          <motion.div 
            style={styles.jobsGrid}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {filteredJobs.map(job => (
              <motion.div
                key={job.id}
                style={styles.jobCard(selectedJob?.id === job.id)}
                onClick={() => handleJobSelect(job)}
                whileHover={{ y: -5, boxShadow: "0 15px 40px rgba(30, 58, 138, 0.25)" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div style={styles.jobTypeBadge(job.job_type)}>
                  {job.job_type.replace('_', ' ')}
                </div>
                
                <h3 style={styles.jobTitle}>{job.title}</h3>
                
                <div style={styles.jobLocation}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={ACCENT_BLUE}/>
                  </svg>
                  {job.location}
                </div>
                
                <p style={styles.jobDescription}>
                  {job.description.length > (filteredJobs.length === 1 && !isMobile ? 400 : 200) 
                    ? `${job.description.substring(0, filteredJobs.length === 1 && !isMobile ? 400 : 200)}...` 
                    : job.description}
                </p>
                
                <div style={styles.jobMeta}>
                  <div style={styles.metaItem}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#6B7280" strokeWidth="1.5"/>
                    </svg>
                    Posted: {new Date(job.posted_date).toLocaleDateString()}
                  </div>
                </div>
                
                <button
                  style={styles.applyButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJobSelect(job);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(30, 58, 138, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {selectedJob?.id === job.id ? "Selected • Click to Apply" : "Apply Now"}
                </button>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div 
            style={styles.noJobsMessage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {activeCategory === 'all' 
              ? "No open positions at the moment. Please check back later!"
              : `No ${activeCategory.replace('_', ' ')} positions available. Try another category.`}
          </motion.div>
        )}

        {/* Application Form Section */}
        <div style={styles.applicationSection} ref={applicationSectionRef}>
          <AnimatePresence>
            {isFormVisible ? (
              <motion.div
                ref={formRef}
                style={styles.applicationFormSection}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.3 }}
              >
                <div style={styles.formHeader}>
                  <h3 style={styles.formTitle}>
                    {selectedJob ? `Apply for ${selectedJob.title}` : "Application Form"}
                  </h3>
                  <button
                    onClick={handleCloseForm}
                    style={styles.closeButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = ACCENT_BLUE;
                      e.currentTarget.style.color = WHITE;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = ACCENT_BLUE;
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Close Form
                  </button>
                </div>
                
                {selectedJob && (
                  <div style={styles.selectedJobTitle}>
                    {selectedJob.title} • {selectedJob.location} • {selectedJob.job_type.replace('_', ' ')}
                  </div>
                )}
                
                {!selectedJob ? (
                  <p style={{ textAlign: "center", color: ACCENT_BLUE, marginBottom: "30px" }}>
                    Please select a job position from the list above to apply.
                  </p>
                ) : (
                  <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formRow}>
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <label style={styles.label}>
                          Full Name <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          type="text"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          style={styles.input}
                          required
                          whileFocus={{ scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        />
                      </motion.div>
                      
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                      >
                        <label style={styles.label}>
                          Email <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          style={styles.input}
                          required
                          whileFocus={{ scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        />
                      </motion.div>
                    </div>
                    
                    <div style={styles.formRow}>
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                      >
                        <label style={styles.label}>
                          Phone Number <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          style={styles.input}
                          required
                          whileFocus={{ scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        />
                      </motion.div>
                      
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 }}
                      >
                        <label style={styles.label}>
                          CV Upload <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          id="cv-upload"
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          style={styles.fileInput}
                          required
                          whileHover={{ scale: 1.02 }}
                        />
                        <p style={styles.fileInfo}>Accepted formats: PDF, DOC, DOCX (Max: 10MB)</p>
                      </motion.div>
                    </div>
                    
                    <motion.div 
                      style={styles.formGroup}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 }}
                    >
                      <label style={styles.label}>
                        Cover Letter <span style={styles.required}>*</span>
                      </label>
                      <motion.textarea
                        name="cover_letter"
                        value={formData.cover_letter}
                        onChange={handleInputChange}
                        style={styles.textarea}
                        required
                        whileFocus={{ scale: 1.01 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        placeholder="Tell us why you're the perfect fit for this position..."
                      />
                    </motion.div>
                    
                    <div style={styles.formRow}>
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.5 }}
                      >
                        <label style={styles.label}>
                          LinkedIn Profile <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          type="url"
                          name="linkedin_profile"
                          value={formData.linkedin_profile}
                          onChange={handleInputChange}
                          style={styles.input}
                          required
                          whileFocus={{ scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                        <p style={styles.fileInfo}>Must be a valid LinkedIn profile URL (e.g., linkedin.com/in/username)</p>
                      </motion.div>
                      
                      <motion.div 
                        style={styles.formGroup}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                      >
                        <label style={styles.label}>
                          Portfolio URL <span style={styles.required}>*</span>
                        </label>
                        <motion.input
                          type="url"
                          name="portfolio_url"
                          value={formData.portfolio_url}
                          onChange={handleInputChange}
                          style={styles.input}
                          required
                          whileFocus={{ scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          placeholder="https://yourportfolio.com"
                        />
                        <p style={styles.fileInfo}>Must be a valid URL to your portfolio or GitHub</p>
                      </motion.div>
                    </div>
                    
                    <motion.button
                      type="submit"
                      style={styles.submitButton}
                      disabled={isSubmitting}
                      whileHover={!isSubmitting ? { scale: 1.05, y: -2 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.95 } : {}}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </motion.button>
                    
                    {submitStatus && (
                      <motion.div 
                        style={{
                          ...styles.statusMessage,
                          background: submitStatus.type === 'success' ? '#D1FAE5' : '#FEE2E2',
                          color: submitStatus.type === 'success' ? '#065F46' : '#991B1B',
                          border: `1px solid ${submitStatus.type === 'success' ? '#A7F3D0' : '#FECACA'}`,
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {submitStatus.message}
                      </motion.div>
                    )}
                  </form>
                )}
              </motion.div>
            ) : (
              // Show a button to open the form if a job is selected but form is hidden
              selectedJob && (
                <motion.button
                  style={styles.showFormButton}
                  onClick={() => setIsFormVisible(true)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(255, 215, 0, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 5px 20px rgba(255, 215, 0, 0.3)';
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Show Application Form for {selectedJob.title}
                </motion.button>
              )
            )}
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
};

export default Careers;